import React, { useState, useEffect } from "react";
import { storage } from "../../../firebase";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { collection, getDocs, query, where } from "firebase/firestore";
import { firestore } from "../../../firebase";

import { Autocomplete, Box } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { format } from "date-fns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

function AddItemPopup({ open, onClose, onSave, item }) {
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [itemMake, setItemMake] = useState("");
  const [model, setModel] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [productName, setProductName] = useState("");
  const [warrantyPeriod, setWarrantyPeriod] = useState("");
  const [warrantyExpiryDate, setWarrantyExpiryDate] = useState(null);
  const [error, setError] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityName, setSelectedFacilityName] = useState("");
  const [selectedBranchName, setSelectedBranchName] = useState("");
  const [facilityRegion, setFacilityRegion] = useState("");
  const [facilityPhoneNumber, setFacilityPhoneNumber] = useState("");
  const [facilityEmail, setFacilityEmail] = useState("");

  // Get unique facility names from facilities list
  const uniqueFacilityNames = [
    ...new Map(
      facilities.map((f) => [f.facilityName, f])
    ).values(),
  ];

  // Filter branches based on selected facility
  const filteredBranches = selectedFacilityName
    ? facilities.filter(
        (f) => f.facilityName === selectedFacilityName
      )
    : [];

  // Updates facility details when facility or branch is selected
  useEffect(() => {
    if (selectedFacilityName && selectedBranchName) {
      const facility = facilities.find(
        (f) =>
          f.facilityName === selectedFacilityName &&
          f.branchName === selectedBranchName
      );
      if (facility) {
        setFacilityRegion(facility.region || "");
        setFacilityPhoneNumber(facility.phoneNumber || "");
        setFacilityEmail(facility.email || "");
      } else {
        setFacilityRegion("");
        setFacilityPhoneNumber("");
        setFacilityEmail("");
      }
    } else {
      setFacilityRegion("");
      setFacilityPhoneNumber("");
      setFacilityEmail("");
    }
  }, [selectedFacilityName, selectedBranchName, facilities]);

  // Calculates and sets the warranty period if purchaseDate or warrantyExpiryDate changes
  useEffect(() => {
    if (purchaseDate && warrantyExpiryDate) {
      const calculatedWarrantyPeriod = calculateWarrantyPeriod(
        purchaseDate,
        warrantyExpiryDate
      );
      setWarrantyPeriod(calculatedWarrantyPeriod.toString());
    }
  }, [purchaseDate, warrantyExpiryDate]);

  const calculateWarrantyPeriod = (purchaseDate, warrantyExpiryDate) => {
    if (!purchaseDate || !warrantyExpiryDate) return "";

    const start = new Date(purchaseDate);
    const end = new Date(warrantyExpiryDate);

    let months = (end.getFullYear() - start.getFullYear()) * 12;
    months -= start.getMonth();
    months += end.getMonth();

    return months > 0 ? months : 0;
  };

  // Populates form fields when an item is passed for editing
  useEffect(() => {
    if (item) {
      setItemMake(item.itemMake);
      setModel(item.model);
      setPurchaseDate(item.purchaseDate ? new Date(item.purchaseDate) : null);
      setWarrantyExpiryDate(
        item.warrantyExpiryDate ? new Date(item.warrantyExpiryDate) : null
      );
      setCustomerName(item.customerName || "");
      setProductName(item.productName || "");
      setWarrantyPeriod(item.warrantyPeriod || "");
      setImageUrl(item.imageUrl || "");
      setSelectedFacilityName(item.facilityName || "");
      setSelectedBranchName(item.branchName || "");
      setFacilityRegion(item.facilityRegion || "");
      setFacilityPhoneNumber(item.facilityPhoneNumber || "");
      setFacilityEmail(item.facilityEmail || "");
    } else {
      resetForm();
    }
  }, [item]);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (purchaseDate && warrantyPeriod) {
      const expiryDate = new Date(purchaseDate);
      expiryDate.setMonth(expiryDate.getMonth() + parseInt(warrantyPeriod, 10));
      setWarrantyExpiryDate(expiryDate);
    }
  }, [purchaseDate, warrantyPeriod]);

  useEffect(() => {
    const fetchFacilitiesData = async () => {
      try {
        const tenantId = sessionStorage.getItem("tenantId") || "";
        const userRole = String(sessionStorage.getItem("userRole") || "")
          .trim()
          .toLowerCase();

        const allowGlobalFacilityAccess = ["customer", "shop"].includes(userRole);

        const profilesQuery =
          userRole === "superadmin" || allowGlobalFacilityAccess || !tenantId
            ? collection(firestore, "UserProfileMaster")
            : query(
                collection(firestore, "UserProfileMaster"),
                where("tenantId", "==", tenantId)
              );

        const profilesSnapshot = await getDocs(profilesQuery);

        const facilityAdmins = profilesSnapshot.docs
          .map((profileDoc) => ({ id: profileDoc.id, ...profileDoc.data() }))
          .filter((profile) => {
            const normalizedRole = String(profile.role || "")
              .trim()
              .toLowerCase();
            return normalizedRole === "facility_admin" && profile.email;
          });

        setFacilities(facilityAdmins);
      } catch (error) {
        console.error("Error fetching facilities:", error);
        setFacilities([]);
      }
    };

    fetchFacilitiesData();
  }, []);

  // Resizes the uploaded image to reduce file size
  const resizeImage = (file, maxSize) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = () => {
        img.src = reader.result;
      };
      reader.onerror = reject;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const scale = Math.sqrt(maxSize / file.size);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: file.type }));
            } else {
              reject(new Error("Error creating resized image."));
            }
          },
          file.type,
          0.8
        );
      };
      reader.readAsDataURL(file);
    });
  };

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Handles image selection and validation
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileType = file.type;
      const allowedTypes = ["image/jpeg", "image/png"];

      if (!allowedTypes.includes(fileType)) {
        setError("Only JPG, JPEG, and PNG files are allowed.");
        return;
      }

      resizeImage(file, 100 * 1024)
        .then((resizedImage) => {
          setImage(resizedImage);
          setImageUrl(URL.createObjectURL(resizedImage));
          setError("");
        })
        .catch((err) => {
          setError("Error resizing image.");
          console.error("Error resizing image:", err);
        });
    }
  };

  const handleRemoveImage = () => {
    if (imageUrl) {
      const imageRef = ref(
        storage,
        `images/${imageUrl.split("/").pop().split("?")[0]}`
      );

      deleteObject(imageRef)
        .then(() => {
          console.log("Image deleted successfully");
        })
        .catch((error) => {
          console.error("Error deleting image: ", error);
        });
    }

    setImage(null);
    setImageUrl("");
  };

  // Handles the save operation, including validation and image upload
  const handleSave = async () => {
    if (
      !itemMake ||
      !model ||
      !purchaseDate ||
      !warrantyPeriod ||
      !customerName ||
      !productName ||
      !selectedFacilityName ||
      !selectedBranchName
    ) {
      setError("All fields are required.");
      return;
    }

    if (purchaseDate > warrantyExpiryDate) {
      setError(
        "Purchase date cannot be greater than the warranty expiry date."
      );
      setWarrantyPeriod("0");
      return;
    }

    const formattedPurchaseDate = purchaseDate
      ? format(purchaseDate, "dd-MMM-yyyy")
      : "";
    const formattedWarrantyExpiryDate = warrantyExpiryDate
      ? format(warrantyExpiryDate, "dd-MMM-yyyy")
      : "";

    if (!image) {
      saveItem(imageUrl, formattedPurchaseDate, formattedWarrantyExpiryDate);
      return;
    }

    try {
      const uniqueImageName = `${Date.now()}-${image.name}`;
      const storageRef = ref(storage, `images/${uniqueImageName}`);
      const uploadTask = uploadBytesResumable(storageRef, image);

      const downloadURL = await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          () => {},
          (uploadError) => reject(uploadError),
          async () => {
            try {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            } catch (downloadError) {
              reject(downloadError);
            }
          }
        );
      });

      setImageUrl(downloadURL);
      saveItem(downloadURL, formattedPurchaseDate, formattedWarrantyExpiryDate);
    } catch (uploadError) {
      console.error("Image upload error:", uploadError);
      if (uploadError?.code === "storage/quota-exceeded") {
        try {
          const inlineImageUrl = await fileToDataUrl(image);
          setImageUrl(inlineImageUrl);
          saveItem(
            inlineImageUrl,
            formattedPurchaseDate,
            formattedWarrantyExpiryDate
          );
        } catch (inlineImageError) {
          console.error("Inline image fallback failed:", inlineImageError);
          setError(
            "Image upload failed: storage quota exceeded. Please try again later or contact admin."
          );
        }
      } else {
        setError("Image upload failed. Please try again.");
      }
    }
  };

  const saveItem = (downloadURL, purchaseDate, warrantyExpiryDate) => {
    const selectedFacility = facilities.find(
      (entry) =>
        entry.facilityName === selectedFacilityName &&
        entry.branchName === selectedBranchName
    );

    onSave({
      ...item,
      itemMake,
      model,
      imageUrl: downloadURL,
      purchaseDate,
      warrantyExpiryDate,
      customerName,
      productName,
      warrantyPeriod,
      facilityName: selectedFacilityName,
      branchName: selectedBranchName,
      facilityRegion,
      facilityPhoneNumber,
      facilityEmail,
      facilityTenantId: selectedFacility?.tenantId || "",
      tenantId: selectedFacility?.tenantId || "",
      updatedAt: new Date(),
    });
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setItemMake("");
    setModel("");
    setPurchaseDate(null);
    setWarrantyExpiryDate(null);
    setCustomerName("");
    setProductName("");
    setWarrantyPeriod("");
    setImage(null);
    setImageUrl("");
    setSelectedFacilityName("");
    setSelectedBranchName("");
    setFacilityRegion("");
    setFacilityPhoneNumber("");
    setFacilityEmail("");
    setError("");
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{item ? "Edit Item" : "Add New Item"}</DialogTitle>
      <DialogContent>
        {error && <Typography color="error">{error}</Typography>}
        <TextField
          margin="dense"
          label="Customer Name"
          type="text"
          fullWidth
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          style={{ paddingBottom: "3px" }}
        />

        <Typography variant="subtitle1" style={{ marginTop: "30px" }}>
          Product Details
        </Typography>
        <Divider style={{ margin: "10px 0", borderBottomWidth: 2 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <TextField
            margin="dense"
            label="Make"
            type="text"
            fullWidth
            value={itemMake}
            onChange={(e) => setItemMake(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Model"
            type="text"
            fullWidth
            value={model}
            onChange={(e) => setModel(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Product Name"
            type="text"
            fullWidth
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
          />
        </div>

        <Typography variant="subtitle1" style={{ marginTop: "30px" }}>
          Establishment Details
        </Typography>
        <Divider style={{ margin: "10px 0", borderBottomWidth: 2 }} />
        <div style={{ gap: "10px" }}>
          <Autocomplete
            fullWidth
            options={uniqueFacilityNames.map((f) => f.facilityName)}
            value={selectedFacilityName}
            onChange={(_, newValue) => {
              setSelectedFacilityName(newValue || "");
              setSelectedBranchName(""); // Reset branch when facility changes
            }}
            filterOptions={(options, state) => {
              const queryText = String(state.inputValue || "").trim().toLowerCase();
              if (!queryText) return options;
              return options.filter((option) =>
                String(option).toLowerCase().includes(queryText)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Facility Name"
                margin="dense"
                required
                style={{ marginTop: "5px", paddingBottom: "6px" }}
              />
            )}
          />
          <Autocomplete
            fullWidth
            options={filteredBranches.map((f) => f.branchName)}
            value={selectedBranchName}
            onChange={(_, newValue) => {
              setSelectedBranchName(newValue || "");
            }}
            disabled={!selectedFacilityName}
            filterOptions={(options, state) => {
              const queryText = String(state.inputValue || "").trim().toLowerCase();
              if (!queryText) return options;
              return options.filter((option) =>
                String(option).toLowerCase().includes(queryText)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Branch Name"
                margin="dense"
                required
                style={{ marginTop: "5px", paddingBottom: "6px" }}
              />
            )}
          />
          <TextField
            margin="dense"
            label="Region"
            type="text"
            fullWidth
            value={facilityRegion}
            InputProps={{
              readOnly: true,
            }}
            style={{ paddingBottom: "6px" }}
          />
          <TextField
            margin="dense"
            label="Admin Phone Number"
            type="text"
            fullWidth
            value={facilityPhoneNumber}
            InputProps={{
              readOnly: true,
            }}
            style={{ paddingBottom: "6px" }}
          />
          <TextField
            margin="dense"
            label="Admin Email"
            type="email"
            fullWidth
            value={facilityEmail}
            InputProps={{
              readOnly: true,
            }}
            style={{ paddingBottom: "6px" }}
          />
        </div>

        <div>
          <Typography variant="subtitle1" style={{ marginTop: "30px" }}>
            Warranty Details
          </Typography>
          <Divider style={{ margin: "10px 0", borderBottomWidth: 2 }} />
          <TextField
            margin="dense"
            label="Warranty Period (months)"
            type="number"
            fullWidth
            value={warrantyPeriod}
            onChange={(e) => setWarrantyPeriod(e.target.value)}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mt: 2,
                mb: 3,
              }}
            >
              <DatePicker
                label="Purchase Date"
                value={purchaseDate}
                onChange={(newDate) => setPurchaseDate(newDate)}
                format="dd-MMM-yyyy"
                renderInput={(params) => <TextField {...params} fullWidth />}
              />

              <DatePicker
                label="Warranty Expiry Date"
                value={warrantyExpiryDate}
                disabled={!purchaseDate}
                onChange={(newDate) => setWarrantyExpiryDate(newDate)}
                format="dd-MMM-yyyy"
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Box>
          </LocalizationProvider>
        </div>

        {imageUrl && (
          <img
            src={imageUrl}
            alt="Item"
            style={{ width: "100%", marginBottom: 16 }}
          />
        )}
        <Button variant="contained" component="label">
          Upload warranty card
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleImageChange}
          />
        </Button>
        {imageUrl && (
          <Button onClick={handleRemoveImage} color="error">
            Remove warranty card
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddItemPopup;
